import { useRef } from 'react';
import { AlertTriangle } from 'lucide-react';
import type { DetailRow, PaneData } from '../../shared/contracts';
import type { PaneDescriptor } from '../../shared/pane-catalog';
import { usePane } from '../lib/queries';
import { paneSpan } from '../lib/layout';
import { Pane } from './Pane';
import { PaneSkeleton } from './PaneSkeleton';

export type PaneViewState = 'loading' | 'error' | 'ready';

/**
 * Decides what a pane loader shows from the value it should DISPLAY (current data, or the
 * retained last-good value). Only a pane that has never loaded shows the error card.
 */
export function paneViewState(query: { isLoading: boolean; data: PaneData | undefined }): PaneViewState {
  if (query.isLoading) return 'loading';
  if (!query.data) return 'error';
  return 'ready';
}

/** Loads a single pane independently: skeleton while loading, fades the pane in when ready. */
export function PaneLoader({
  descriptor,
  onDrill,
  onOpslag,
  showEyebrow = true,
}: {
  descriptor: PaneDescriptor;
  onDrill: (pane: PaneData, bucket?: string) => void;
  onOpslag: (row: DetailRow) => void;
  /** The agenda/board row suppresses the department eyebrow; the KPI grid shows it. */
  showEyebrow?: boolean;
}) {
  const { data, isLoading } = usePane(descriptor.id);
  // Retain the last successfully-loaded pane. keepPreviousData covers a period change while
  // it LOADS, but if that new fetch then errors React Query drops data to undefined — so we
  // fall back to the last-good value rather than blanking a good pane to an error card.
  const lastGood = useRef<PaneData | undefined>(undefined);
  if (data) lastGood.current = data;
  const pane = data ?? lastGood.current;
  const view = paneViewState({ isLoading, data: pane });

  if (view === 'loading') return <PaneSkeleton kind={descriptor.kind} />;
  if (view === 'error' || !pane) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-2 rounded-brand border border-brand-primary/10 bg-white p-5 text-center text-sm text-brand-dark/50 shadow-sm">
        <AlertTriangle size={18} className="text-brand-error" />
        {descriptor.title} kunne ikke hentes.
      </div>
    );
  }
  return (
    <Pane
      pane={pane}
      wide={paneSpan(descriptor) >= 2}
      onDrill={(bucket) => onDrill(pane, bucket)}
      onOpslag={onOpslag}
      showEyebrow={showEyebrow}
    />
  );
}
